import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { useSiteMedia } from "@/lib/siteImages";
import Reveal from "@/components/Reveal";

export default function Science() {
  const { t } = useLanguage();
  const media = useSiteMedia();
  const pillars = [
    { t: t.science.p1t, x: t.science.p1x },
    { t: t.science.p2t, x: t.science.p2x },
    { t: t.science.p3t, x: t.science.p3x },
    { t: t.science.p4t, x: t.science.p4x },
  ];
  const certs = ["GMP Certified", "EU Registered (GR · DE · BG)", "Health Canada NPN", "TGA Australia", "FDA Registered Facility", "Third-Party Tested"];

  return (
    <div data-testid="science-page" className="bg-white">
      <section className="relative overflow-hidden bg-navy pb-24 pt-28 text-white noise-bg lg:pb-32">
        <div className="spotlight absolute inset-0" />
        <span aria-hidden className="text-ghost-light pointer-events-none absolute -bottom-10 end-0 select-none font-display text-[15vw] italic leading-none">
          Qualitas
        </span>
        <div className="relative mx-auto max-w-7xl px-6">
          <Reveal><p className="label-luxe text-gold">{t.science.kicker}</p></Reveal>
          <Reveal delay={1}>
            <h1 className="mt-6 max-w-3xl font-display text-5xl font-light tracking-tight sm:text-6xl">{t.science.title}</h1>
          </Reveal>
          <Reveal delay={2}>
            <p className="mt-8 max-w-xl text-base font-light leading-relaxed text-white/55">{t.science.lead}</p>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-24 lg:py-32">
        <div className="grid gap-px bg-navy/10 lg:grid-cols-2">
          {pillars.map((p, i) => (
            <Reveal key={i} delay={i} className="bg-white p-10 lg:p-14">
              <p className="font-display text-5xl font-light text-gold/60">0{i + 1}</p>
              <h2 className="mt-8 font-display text-2xl font-medium text-navy">{p.t}</h2>
              <p className="mt-4 max-w-md text-sm font-light leading-relaxed text-navy/55">{p.x}</p>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="border-y border-navy/10 bg-surface py-24">
        <div className="mx-auto grid max-w-7xl items-center gap-16 px-6 lg:grid-cols-2">
          <Reveal>
            <div className="img-zoom-frame border border-navy/10">
              <img src={media("science-lab")} alt="GMP laboratory" data-testid="science-image" className="aspect-[4/3] w-full object-cover" loading="lazy" />
            </div>
          </Reveal>
          <div>
            <Reveal>
              <h2 className="font-display text-3xl font-light tracking-tight text-navy sm:text-4xl">{t.science.certsT}</h2>
            </Reveal>
            <div className="mt-8 space-y-3">
              {certs.map((c, i) => (
                <Reveal key={i} delay={i} className="flex items-center gap-3 border border-navy/10 bg-white px-5 py-4">
                  <ShieldCheck size={16} className="shrink-0 text-gold-dark" />
                  <span data-testid={`science-cert-${i}`} className="text-sm font-medium text-navy/80">{c}</span>
                </Reveal>
              ))}
            </div>
            <Reveal delay={6} className="mt-10">
              <Link to="/shop" data-testid="science-cta" className="btn-primary-luxe">
                {t.science.cta}
                <ArrowRight size={14} className="rtl:rotate-180" />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
}
